// pages/admin-dashboard.ts — staff landing screen.
//
// Mirrors frontend2/Admin.jsx :: AdminDashboard.
//   • Greeting + "X grants pending review" lede
//   • 4 stat tiles: Pending review · Action required · Approved (year) · Approved giving
//   • Priority queue (api.adminQueue), top 8 as cards
//   • All-applications table with status-filter chip bar
//
// Both calls run in parallel: adminQueue() seeds the priority list,
// listGrants() seeds the table + stats. Role-gated to approver / admin.

import type { Page, PageContext } from '../router';
import { html, esc, firstNameOf, fmtDate, fmtMoneyOrDash, statusPillClass } from '../util';

interface Grant {
  GrantId: number;
  Status: string;
  ProjectName: string;
  OrganizationLegalName?: string;
  RequestorName?: string;
  ReviewerName?: string | null;
  RequestedAmount: number | null;
  ApprovedAmount: number | null;
  SubmittedAt?: string;
  ReviewedAt?: string;
  UpdatedAt?: string;
}

const STATUS_FILTERS = ['All', 'Submitted', 'Under Review', 'Action Required', 'Approved'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];

const IRS_TARGET = 50000;
const PAGE_SIZE  = 25;

function adminStat(label: string, value: string, sub: string, accent?: 'action'): string {
  const color = accent === 'action' ? 'var(--st-action)' : 'var(--primary)';
  return html`
    <div class="card" style="padding: 28px 28px;">
      <div class="label">${label}</div>
      <div class="serif" style="font-size: 38px; line-height: 1; color: ${{ __raw: color }}; margin-top: 12px;">${value}</div>
      ${{ __raw: sub ? html`<div class="t-small" style="margin-top: 12px;">${sub}</div>` : '' }}
    </div>
  `;
}

function approvedGivingTile(sum: number, pct: number): string {
  return html`
    <div class="card" style="padding: 28px 28px;">
      <div class="label">Approved giving · year</div>
      <div class="serif" style="font-size: 32px; line-height: 1; color: var(--accent); margin-top: 12px;">
        ${fmtMoneyOrDash(sum)}
        <span class="t-small" style="color: var(--ink-soft);">/ ${fmtMoneyOrDash(IRS_TARGET)}</span>
      </div>
      <div style="width: 100%; height: 8px; background: var(--paper-3); border-radius: 4px;
                  overflow: hidden; margin: 12px 0 6px;">
        <div style="width: ${pct}%; height: 100%; background: var(--accent); border-radius: 4px;"></div>
      </div>
      <p class="t-small" style="margin: 0;">${pct}% of loose annual target — see Funding target for precise.</p>
    </div>
  `;
}

function queueCard(g: Grant): string {
  return html`
    <article class="card ad-queue-card" data-grant-id="${g.GrantId}"
             style="padding: 24px; cursor: pointer;">
      <span class="pill ${statusPillClass(g.Status)}">${g.Status}</span>
      <h3 class="t-h4 serif" style="margin: 16px 0 0; font-size: 18px; line-height: 1.3;">${g.ProjectName}</h3>
      <p class="t-small" style="margin-top: 4px;">${g.OrganizationLegalName || '\u2014'}</p>
      <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-top: 16px;">
        <span class="serif" style="font-size: 20px; color: var(--primary);">${fmtMoneyOrDash(g.RequestedAmount)}</span>
        <span class="t-small">${fmtDate(g.SubmittedAt)}</span>
      </div>
    </article>
  `;
}

function tableRow(g: Grant): string {
  return html`
    <tr style="cursor: pointer;" data-grant-id="${g.GrantId}">
      <td class="num">${fmtDate(g.SubmittedAt)}</td>
      <td style="padding-right: 24px;">
        <div class="serif" style="font-size: 18px; line-height: 1.3;">${g.ProjectName}</div>
        <p class="t-small" style="margin: 4px 0 0;">${g.OrganizationLegalName || ''}</p>
      </td>
      <td><span style="color: var(--ink-2);">${g.RequestorName || '\u2014'}</span></td>
      <td class="num">${fmtMoneyOrDash(g.ApprovedAmount ?? g.RequestedAmount)}</td>
      <td><span class="pill ${statusPillClass(g.Status)}">${g.Status}</span></td>
      <td><span class="t-small">${g.ReviewerName || 'Unassigned'}</span></td>
      <td><span style="color: var(--ink-mute);">→</span></td>
    </tr>
  `;
}

const AdminDashboard: Page = {
  guard(ctx: PageContext): string | null {
    if (!ctx.user) return 'login';
    if (ctx.userRole !== 'admin' && ctx.userRole !== 'approver') return 'landing';
    return null;
  },

  render(ctx: PageContext): string {
    const user = ctx.user as { fullName?: string; FullName?: string; email?: string; Email?: string } | null;
    const first = firstNameOf(user?.fullName || user?.FullName || user?.email || user?.Email || '');

    return html`
      <main class="page">
        <section style="padding: 56px 0 16px;">
          <div class="wrap">
            <div class="eyebrow">Grants</div>
            <div class="d-flex" style="justify-content: space-between; align-items: end;
                                      flex-wrap: wrap; gap: 24px; margin-top: 16px;">
              <div>
                <h1 class="t-h1" style="margin: 0; font-size: clamp(40px, 5vw, 72px);">Good day, ${first}.</h1>
                <p class="t-lede" id="ad-lede" style="margin-top: 16px;">Loading the queue…</p>
              </div>
              <a class="btn btn-primary" data-nav="grant-form">+ New grant request</a>
            </div>
          </div>
        </section>

        <section style="padding: 32px 0;">
          <div class="wrap">
            <div id="ad-stat-tiles" class="ad-stat-grid"
                 style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;">
              ${{ __raw: [1,2,3,4].map(() => adminStat('', '—', '')).join('') }}
            </div>
          </div>
        </section>

        <section style="padding: 48px 0 24px;">
          <div class="wrap">
            <div class="d-flex" style="justify-content: space-between; align-items: baseline; margin-bottom: 24px;">
              <h2 class="t-h2" style="margin: 0; font-size: 36px;">Priority queue</h2>
              <div class="t-small" id="ad-queue-count">…</div>
            </div>
            <div id="ad-queue"><p class="t-body">Loading…</p></div>
          </div>
        </section>

        <section style="padding: 32px 0 96px;">
          <div class="wrap">
            <div class="d-flex" style="justify-content: space-between; align-items: baseline;
                                       margin-bottom: 16px; flex-wrap: wrap; gap: 12px;">
              <h2 class="t-h2" style="margin: 0; font-size: 36px;">All applications</h2>
              <div id="ad-filters" class="card d-flex"
                   style="padding: 6px; gap: 4px; flex-wrap: wrap;"></div>
            </div>
            <div style="margin-bottom: 16px;">
              <input id="ad-search" class="form-control"
                     placeholder="Search project, requestor, assignee…"
                     style="width: 320px; max-width: 100%; padding: 8px 12px;"/>
            </div>
            <div id="ad-table"></div>
            <div class="d-flex" style="justify-content: space-between; align-items: center;
                                       margin-top: 16px; flex-wrap: wrap; gap: 12px;">
              <div class="t-small" id="ad-table-meta"></div>
              <div class="d-flex" id="ad-pager" style="gap: 8px; align-items: center;"></div>
            </div>
          </div>
        </section>
      </main>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    let alive = true;
    let statusFilter: StatusFilter = 'All';
    let search = '';
    let page = 1;
    let queue: Grant[] = [];
    let allGrants: Grant[] = [];

    const ledeEl    = root.querySelector('#ad-lede')        as HTMLElement | null;
    const tilesEl   = root.querySelector('#ad-stat-tiles')  as HTMLElement | null;
    const queueEl   = root.querySelector('#ad-queue')       as HTMLElement | null;
    const queueCt   = root.querySelector('#ad-queue-count') as HTMLElement | null;
    const filtersEl = root.querySelector('#ad-filters')     as HTMLElement | null;
    const searchEl  = root.querySelector('#ad-search')      as HTMLInputElement | null;
    const tableEl   = root.querySelector('#ad-table')       as HTMLElement | null;
    const tableMeta = root.querySelector('#ad-table-meta')  as HTMLElement | null;
    const pagerEl   = root.querySelector('#ad-pager')       as HTMLElement | null;

    function paintFilters(): void {
      if (!filtersEl) return;
      const counts: Record<StatusFilter, number> = {
        All:               allGrants.length,
        Submitted:         allGrants.filter(g => g.Status === 'Submitted').length,
        'Under Review':    allGrants.filter(g => g.Status === 'Under Review').length,
        'Action Required': allGrants.filter(g => g.Status === 'Action Required').length,
        Approved:          allGrants.filter(g => g.Status === 'Approved').length,
      };
      filtersEl.innerHTML = STATUS_FILTERS.map(s => {
        const active = statusFilter === s;
        return `
          <button type="button" data-filter="${esc(s)}"
                  style="padding: 8px 14px; font-size: 13px; font-weight: 500; border: none;
                         cursor: pointer; border-radius: 999px;
                         background: ${active ? 'var(--primary)' : 'transparent'};
                         color: ${active ? 'var(--paper)' : 'var(--ink-2)'};">
            ${esc(s)} <span style="opacity: 0.7; margin-left: 4px;">(${counts[s] ?? 0})</span>
          </button>
        `;
      }).join('');
    }

    function filteredGrants(): Grant[] {
      const lc = search.trim().toLowerCase();
      return allGrants.filter(g => {
        if (statusFilter !== 'All' && g.Status !== statusFilter) return false;
        if (!lc) return true;
        return (g.ProjectName   || '').toLowerCase().includes(lc)
            || (g.RequestorName || '').toLowerCase().includes(lc)
            || (g.ReviewerName  || '').toLowerCase().includes(lc);
      });
    }

    function paintTable(): void {
      if (!tableEl || !tableMeta || !pagerEl) return;
      const filtered  = filteredGrants();
      const totalPgs  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
      if (page > totalPgs) page = totalPgs;
      if (page < 1) page = 1;
      const start     = (page - 1) * PAGE_SIZE;
      const pageRows  = filtered.slice(start, start + PAGE_SIZE);

      if (filtered.length === 0) {
        const empty = allGrants.length === 0
          ? 'No grants yet.'
          : (search.trim()
              ? `No grants match &ldquo;${esc(search)}&rdquo;${statusFilter === 'All' ? '' : ` in the &ldquo;${esc(statusFilter)}&rdquo; filter`}.`
              : `No grants match the &ldquo;${esc(statusFilter)}&rdquo; filter.`);
        tableEl.innerHTML = `
          <div class="card" style="padding: 48px 40px; text-align: center;">
            <p class="t-body" style="margin: 0;">${empty}</p>
          </div>
        `;
      } else {
        tableEl.innerHTML = `
          <table class="ledger">
            <thead>
              <tr>
                <th style="width: 140px;">Submitted</th>
                <th>Project</th>
                <th>Requestor</th>
                <th style="width: 120px;">Amount</th>
                <th style="width: 160px;">Status</th>
                <th style="width: 160px;">Assigned to</th>
                <th style="width: 60px;"></th>
              </tr>
            </thead>
            <tbody>${pageRows.map(tableRow).join('')}</tbody>
          </table>
        `;
      }

      if (filtered.length === 0) {
        tableMeta.textContent = `0 of ${allGrants.length} grants`;
      } else {
        const from = start + 1;
        const to   = start + pageRows.length;
        tableMeta.textContent = `Showing ${from}–${to} of ${filtered.length}`
          + (filtered.length !== allGrants.length ? ` (filtered from ${allGrants.length})` : '');
      }

      if (totalPgs <= 1) {
        pagerEl.innerHTML = '';
      } else {
        const btn = (label: string, action: string, disabled: boolean): string => `
          <button type="button" data-page-action="${action}" ${disabled ? 'disabled' : ''}
                  style="padding: 6px 12px; border: 1px solid var(--rule); background: var(--paper);
                         border-radius: 8px; font-size: 13px; cursor: ${disabled ? 'default' : 'pointer'};
                         color: ${disabled ? 'var(--ink-mute)' : 'var(--ink-2)'};
                         opacity: ${disabled ? 0.5 : 1};">${label}</button>
        `;
        pagerEl.innerHTML = `
          ${btn('&larr; Prev', 'prev', page <= 1)}
          <span class="t-small" style="min-width: 96px; text-align: center;">Page ${page} of ${totalPgs}</span>
          ${btn('Next &rarr;', 'next', page >= totalPgs)}
        `;
      }
    }

    function paintAll(): void {
      paintFilters();
      paintTable();
    }

    // Delegated click — any row/card with data-grant-id, plus filter chips and pager
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      const filterBtn = t.closest('[data-filter]') as HTMLElement | null;
      if (filterBtn) {
        statusFilter = (filterBtn.getAttribute('data-filter') as StatusFilter) || 'All';
        page = 1;
        paintAll();
        return;
      }

      const pagerBtn = t.closest('[data-page-action]') as HTMLButtonElement | null;
      if (pagerBtn) {
        if (pagerBtn.disabled) return;
        const action = pagerBtn.getAttribute('data-page-action');
        if (action === 'prev') page = Math.max(1, page - 1);
        else if (action === 'next') page = page + 1;
        paintTable();
        return;
      }

      const gEl = t.closest('[data-grant-id]') as HTMLElement | null;
      if (gEl) {
        const id = Number(gEl.getAttribute('data-grant-id'));
        if (Number.isFinite(id)) ctx.navigate('grant-review', { id });
      }
    });

    if (searchEl) {
      searchEl.addEventListener('input', () => {
        search = searchEl.value || '';
        page = 1;
        paintTable();
      });
    }

    Promise.all([window.api.adminQueue(), window.api.listGrants()])
      .then(([q, g]) => {
        if (!alive) return;
        queue     = (Array.isArray(q) ? q : []) as Grant[];
        allGrants = (Array.isArray(g) ? g : []) as Grant[];

        // Lede
        if (ledeEl) {
          ledeEl.innerHTML = queue.length === 0
            ? 'Nothing pending review right now.'
            : `You have <b style="color: var(--st-action);">${queue.length}</b> grant${queue.length === 1 ? '' : 's'} pending review in the pipeline.`;
        }

        // Stats
        const year     = new Date().getFullYear();
        const approved = allGrants.filter(x => x.Status === 'Approved'
          && x.ReviewedAt && new Date(x.ReviewedAt).getFullYear() === year);
        const irsSum   = approved.reduce((s, x) => s + Number(x.ApprovedAmount || 0), 0);
        const irsPct   = Math.min(100, Math.round((irsSum / IRS_TARGET) * 100));
        const actionRequired = queue.filter(x => x.Status === 'Action Required').length;

        if (tilesEl) {
          tilesEl.innerHTML = [
            adminStat('Pending review',     String(queue.length),
                      queue.length === 0 ? 'queue clear' : 'awaiting first pass'),
            adminStat('Action required',    String(actionRequired),
                      actionRequired === 0 ? 'no follow-ups open' : 'applicant follow-ups',
                      'action'),
            adminStat('Approved this year', String(approved.length),
                      approved.length === 0 ? 'first year' : `since Jan ${year}`),
            approvedGivingTile(irsSum, irsPct),
          ].join('');
        }

        // Queue
        if (queueEl) {
          if (queue.length === 0) {
            queueEl.innerHTML = `
              <div class="card" style="padding: 48px 40px; text-align: center;">
                <p class="t-body" style="margin: 0;">The queue is clear. New submissions will appear here.</p>
              </div>
            `;
          } else {
            queueEl.innerHTML = `
              <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px;">
                ${queue.slice(0, 8).map(queueCard).join('')}
              </div>
            `;
          }
        }
        if (queueCt) queueCt.textContent = `${queue.length} pending`;

        paintAll();
      })
      .catch(err => {
        if (!alive) return;
        if (ledeEl) ledeEl.textContent = '';
        if (queueEl) {
          queueEl.innerHTML = `<p class="t-body" style="color: var(--st-denied);">Could not load queue (${esc((err as Error).message || 'network')}).</p>`;
        }
      });

    (AdminDashboard as Page & { _alive?: () => void })._alive = () => { alive = false; };
  },

  unmount(): void {
    const stop = (AdminDashboard as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default AdminDashboard;
